import { useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { Calendar, Plus, Trash2, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RenewAvailabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingId: string;
  listingTitle: string;
}

export default function RenewAvailabilityDialog({
  open,
  onOpenChange,
  listingId,
  listingTitle,
}: RenewAvailabilityDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dateRanges, setDateRanges] = useState<Array<{ from: Date; to: Date }>>([]);
  const [currentRange, setCurrentRange] = useState<{ from?: Date; to?: Date }>({});

  const addDateRange = () => {
    if (currentRange.from && currentRange.to) {
      setDateRanges([...dateRanges, { from: currentRange.from, to: currentRange.to }]);
      setCurrentRange({});
    }
  };

  const removeDateRange = (index: number) => {
    setDateRanges(dateRanges.filter((_, i) => i !== index));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (dateRanges.length === 0) {
        throw new Error("Tilføj mindst én datoperiode");
      }

      const availabilityRecords = dateRanges.map((range) => ({
        listing_id: listingId,
        type: "range" as const,
        start_at: range.from.toISOString(),
        end_at: range.to.toISOString(),
      }));

      const { error } = await supabase.from("availability").insert(availabilityRecords);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Datoer tilføjet",
        description: "Din parkeringsplads er nu tilgængelig på de nye datoer",
      });
      queryClient.invalidateQueries({ queryKey: ["myListings"] });
      queryClient.invalidateQueries({ queryKey: ["listingAvailability"] });
      setDateRanges([]);
      setCurrentRange({});
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Fejl",
        description: error.message || "Kunne ikke tilføje datoer",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tilføj nye datoer</DialogTitle>
          <DialogDescription>
            Tilføj nye tilgængelige perioder for {listingTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Calendar for selecting date range */}
          <div className="space-y-2">
            <Label>Vælg periode</Label>
            <div className="border rounded-lg p-4">
              <CalendarComponent
                mode="range"
                selected={{
                  from: currentRange.from,
                  to: currentRange.to,
                }}
                onSelect={(range) => {
                  if (range) {
                    setCurrentRange({ from: range.from, to: range.to });
                  } else {
                    setCurrentRange({});
                  }
                }}
                locale={da}
                disabled={(date) => date < new Date()}
                className="mx-auto"
              />
            </div>
            
            <Button
              onClick={addDateRange}
              disabled={!currentRange.from || !currentRange.to}
              variant="outline"
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Tilføj periode
            </Button>
          </div>

          {/* List of added date ranges */}
          {dateRanges.length > 0 && (
            <div className="space-y-2">
              <Label>Tilføjede perioder ({dateRanges.length})</Label>
              <div className="space-y-2">
                {dateRanges.map((range, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {format(range.from, "d. MMM yyyy", { locale: da })} -{" "}
                        {format(range.to, "d. MMM yyyy", { locale: da })}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDateRange(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={dateRanges.length === 0 || saveMutation.isPending}
              className="flex-1"
            >
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Gem datoer
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDateRanges([]);
                setCurrentRange({});
                onOpenChange(false);
              }}
              disabled={saveMutation.isPending}
            >
              Annuller
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
